import { startRegistration, startAuthentication } from '@simplewebauthn/browser'
import { getApiEndpoint } from '../config/api'

/**
 * Register a new passkey for the current user
 * @param {string} token - JWT token for authentication
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const registerPasskey = async (token) => {
  try {
    // Step 1: Get registration options from server
    const optionsResponse = await fetch(getApiEndpoint('/passkey/register/options'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include', // IMPORTANT: Include cookies for session
    })

    const optionsData = await optionsResponse.json()

    if (!optionsData.success) {
      throw new Error(optionsData.message || 'Failed to get registration options')
    }

    // Step 2: Use browser WebAuthn API to create credential
    // Use optionsJSON format for SimpleWebAuthn v13+
    const credential = await startRegistration({ optionsJSON: optionsData.options })

    // Step 3: Send credential to server for verification
    const verifyResponse = await fetch(getApiEndpoint('/passkey/register/verify'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include', // IMPORTANT: Include cookies for session
      body: JSON.stringify({ credential }),
    })

    const verifyData = await verifyResponse.json()

    if (!verifyData.success) {
      throw new Error(verifyData.message || 'Failed to verify passkey')
    }

    return { success: true, message: 'Passkey registered successfully!' }
  } catch (error) {
    console.error('Passkey registration error:', error)
    return {
      success: false,
      message: error.message || 'Failed to register passkey',
    }
  }
}

/**
 * Authenticate using a passkey
 * @param {string} email - User's email address
 * @returns {Promise<{success: boolean, user?: object, token?: string, message?: string, status?: string}>}
 */
export const authenticateWithPasskey = async (email) => {
  try {
    // Step 1: Get authentication options from server
    const optionsResponse = await fetch(getApiEndpoint('/passkey/authenticate/options'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // IMPORTANT: Include cookies for session
      body: JSON.stringify({ email }),
    })

    const optionsData = await optionsResponse.json()
    console.log('Options response:', optionsData)

    if (!optionsData.success) {
      throw new Error(optionsData.message || 'No passkeys found for this user')
    }

    console.log('Calling startAuthentication with options:', optionsData.options)

    // Step 2: Use browser WebAuthn API to get credential
    // Use optionsJSON format for SimpleWebAuthn v13+
    const credential = await startAuthentication({ optionsJSON: optionsData.options })
    console.log('startAuthentication returned credential:', credential)

    // Step 3: Send credential to server for verification
    const verifyResponse = await fetch(getApiEndpoint('/passkey/authenticate/verify'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // IMPORTANT: Include cookies for session
      body: JSON.stringify({ credential }),
    })

    const verifyData = await verifyResponse.json()
    console.log('Verify response:', verifyData)

    if (!verifyData.success) {
      return {
        success: false,
        message: verifyData.message,
        status: verifyData.status,
      }
    }

    console.log('Authentication successful! Token:', verifyData.token)

    return {
      success: true,
      user: verifyData.user,
      token: verifyData.token,
    }
  } catch (error) {
    console.error('Passkey authentication error:', error)
    console.error('Error stack:', error.stack)
    return {
      success: false,
      message: error.message || 'Failed to authenticate with passkey',
    }
  }
}

/**
 * Get list of registered passkeys for current user
 * @param {string} token - JWT token for authentication
 * @returns {Promise<{success: boolean, passkeys?: array, message?: string}>}
 */
export const listPasskeys = async (token) => {
  try {
    const response = await fetch(getApiEndpoint('/passkey/list'), {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    })

    const data = await response.json()

    if (!data.success) {
      throw new Error(data.message || 'Failed to list passkeys')
    }

    return { success: true, passkeys: data.passkeys }
  } catch (error) {
    console.error('List passkeys error:', error)
    return {
      success: false,
      message: error.message || 'Failed to list passkeys',
    }
  }
}

/**
 * Remove a passkey
 * @param {string} passkeyId - ID of the passkey to remove
 * @param {string} token - JWT token for authentication
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const removePasskey = async (passkeyId, token) => {
  try {
    const response = await fetch(`/api/passkey/${passkeyId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    })

    const data = await response.json()

    if (!data.success) {
      throw new Error(data.message || 'Failed to remove passkey')
    }

    return { success: true, message: 'Passkey removed successfully' }
  } catch (error) {
    console.error('Remove passkey error:', error)
    return {
      success: false,
      message: error.message || 'Failed to remove passkey',
    }
  }
}
